import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import CustomDatePicker from "../../component/datePicker/DatePicker";
import SearchInput from "../../component/searchInput/SearchInput";
import RadioButton from "../../component/radioButton/RadioButton";
import Text from "../../component/text/Text";
import { APIGetFamilies } from "../../config/utilities/APIs/familyApi";
import { APIGetStops } from "../../config/utilities/APIs/stopsApi";
import {
  formatScheduleOptionLabel,
  getAudienceStudentNamesFromFamily,
  getAudienceStudentNamesFromStop,
  getFamilyDisplayLabel,
  getFamilyShortLabel,
  resolveFamilyLabel,
  getScheduleDisplayLabel,
  getScheduleShortLabel,
  resolveScheduleLabel,
  getStopDisplayLabel,
  getStopShortLabel,
  resolveStopLabel,
  resolveCampusValue,
  getTripTypeLabel,
} from "./campaignFormHelpers.js";
import { CAMPAIGN_CAMPUS_GRADE_OPTIONS } from "./campaignFormConfig.js";
import CampaignAudienceStudentsModal from "./CampaignAudienceStudentsModal";
import "./CampaignAudienceStudentsModal.css";

const SEARCH_DEBOUNCE_MS = 350;
const FAMILY_SEARCH_PAGE_SIZE = 25;
const STOP_SEARCH_PAGE_SIZE = 25;
const ACTIVE_STOP_FILTERS = { status: "active" };

function SelectedChip({ label, onRemove, disabled }) {
  return (
    <span className="campaign-audience-chip">
      <span className="campaign-audience-chip__label">{label}</span>
      {!disabled ? (
        <button
          type="button"
          className="campaign-audience-chip__remove"
          aria-label={`Remove ${label}`}
          onClick={onRemove}
        >
          ×
        </button>
      ) : null}
    </span>
  );
}

function SelectedChipsGroup({ count, children }) {
  return (
    <div className="campaign-audience-selected">
      <p className="campaign-audience-selected__label">Selected ({count})</p>
      <div className="campaign-audience-chips">{children}</div>
    </div>
  );
}

function SearchResultList({
  items,
  selectedIds,
  disabled,
  onToggle,
  onViewStudents,
  emptyMessage,
  loading,
}) {
  if (loading) {
    return <p className="campaign-audience-panel__hint">Searching…</p>;
  }
  if (items.length === 0) {
    return <p className="campaign-audience-panel__hint">{emptyMessage}</p>;
  }

  return (
    <ul className="campaign-audience-results">
      {items.map((item) => {
        const checked = selectedIds.includes(item.id);
        return (
          <li key={item.id}>
            <label
              className={`campaign-audience-result${checked ? " campaign-audience-result--selected" : ""}`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => onToggle(item)}
              />
              <span className="campaign-audience-result__content">
                <span className="campaign-audience-result__title">
                  {item.title}
                </span>
                {item.subtitle ? (
                  <span className="campaign-audience-result__subtitle">
                    {item.subtitle}
                  </span>
                ) : null}
                {onViewStudents && item.students?.length > 0 ? (
                  <button
                    type="button"
                    className="campaign-audience-result__view-students"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onViewStudents(item);
                    }}
                  >
                    View {item.students.length} student
                    {item.students.length === 1 ? "" : "s"}
                  </button>
                ) : null}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

export default function CampaignAudiencePanel({
  targetAudience,
  input,
  setInput,
  mode,
  error,
  scheduleLookup,
  setScheduleLookup,
  scheduleData,
  scheduleLoading,
  tripTypes,
  institutionId,
  datetimeWidth,
  scheduleLabels,
  setScheduleLabels,
  familyLabels,
  setFamilyLabels,
  stopLabels,
  setStopLabels,
}) {
  const readOnly = mode === "view";
  const [familySearch, setFamilySearch] = useState("");
  const [stopSearch, setStopSearch] = useState("");
  const [scheduleSearch, setScheduleSearch] = useState("");
  const [familyResults, setFamilyResults] = useState([]);
  const [stopResults, setStopResults] = useState([]);
  const [familyLoading, setFamilyLoading] = useState(false);
  const [stopLoading, setStopLoading] = useState(false);
  const attemptedFamilyLabelIdsRef = useRef(new Set());
  const attemptedStopLabelIdsRef = useRef(new Set());
  const [studentsModal, setStudentsModal] = useState(null);

  const openStudentsModal = useCallback((item) => {
    if (!item?.students?.length) return;
    setStudentsModal({
      title: item.studentsModalTitle || item.title,
      subtitle: item.studentsModalSubtitle || item.subtitle || null,
      students: item.students,
    });
  }, []);

  const closeStudentsModal = useCallback(() => {
    setStudentsModal(null);
  }, []);

  const tripTypeOptions = useMemo(
    () =>
      (tripTypes || []).map((type) => ({
        id: type.id,
        label: getTripTypeLabel(type.id, tripTypes),
      })),
    [tripTypes],
  );

  const toggleTripType = (tripTypeId) => {
    if (readOnly) return;
    setInput((prev) => {
      const current = Array.isArray(prev.tripTypeIds) ? prev.tripTypeIds : [];
      const next = current.includes(tripTypeId)
        ? current.filter((id) => id !== tripTypeId)
        : [...current, tripTypeId];
      return { ...prev, tripTypeIds: next };
    });
  };

  const selectCampus = (campusValue) => {
    if (readOnly) return;
    setInput((prev) => ({ ...prev, campus: campusValue }));
  };

  const toggleSchedule = useCallback(
    (schedule) => {
      if (readOnly) return;
      const id = schedule?._id;
      if (!id) return;

      setScheduleLabels((prev) => ({
        ...prev,
        [id]: formatScheduleOptionLabel(schedule),
      }));

      setInput((prev) => {
        const current = Array.isArray(prev.scheduleIds) ? prev.scheduleIds : [];
        const next = current.includes(id)
          ? current.filter((item) => item !== id)
          : [...current, id];
        return {
          ...prev,
          scheduleIds: next,
          scheduleId: next[0] || "",
        };
      });
    },
    [readOnly, setScheduleLabels, setInput],
  );

  const scheduleResultItems = useMemo(() => {
    const term = scheduleSearch.trim().toLowerCase();
    return (scheduleData || [])
      .map((option) => option.schedule)
      .filter(Boolean)
      .filter((schedule) => {
        if (!term) return true;
        const haystack = [
          getScheduleShortLabel(schedule),
          getScheduleDisplayLabel(schedule),
          formatScheduleOptionLabel(schedule),
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      })
      .map((schedule) => ({
        id: schedule._id,
        title: getScheduleShortLabel(schedule),
        subtitle: getScheduleDisplayLabel(schedule),
        raw: schedule,
      }));
  }, [scheduleData, scheduleSearch]);

  const getScheduleChipLabel = useCallback(
    (id) =>
      resolveScheduleLabel(id, {
        scheduleLabels,
        scheduleDetails: input.scheduleDetails,
        scheduleOptions: scheduleData,
      }),
    [scheduleData, scheduleLabels, input.scheduleDetails],
  );

  const toggleFamily = useCallback(
    (family) => {
      if (readOnly) return;
      const id = family.familyId || family._id;
      if (!id) return;

      setFamilyLabels((prev) => ({
        ...prev,
        [id]: getFamilyShortLabel(family),
      }));

      setInput((prev) => {
        const current = Array.isArray(prev.familyIds) ? prev.familyIds : [];
        const next = current.includes(id)
          ? current.filter((item) => item !== id)
          : [...current, id];
        return { ...prev, familyIds: next };
      });
    },
    [readOnly, setFamilyLabels, setInput],
  );

  const toggleStop = useCallback(
    (stop) => {
      if (readOnly) return;
      const id = stop._id || stop.id;
      if (!id) return;

      setStopLabels((prev) => ({
        ...prev,
        [id]: getStopShortLabel(stop),
      }));

      setInput((prev) => {
        const current = Array.isArray(prev.stopIds) ? prev.stopIds : [];
        const next = current.includes(id)
          ? current.filter((item) => item !== id)
          : [...current, id];
        return { ...prev, stopIds: next };
      });
    },
    [readOnly, setStopLabels, setInput],
  );

  useEffect(() => {
    if (targetAudience !== "family" || !institutionId) return undefined;

    const term = familySearch.trim();
    if (term.length < 2) {
      setFamilyResults([]);
      return undefined;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      setFamilyLoading(true);
      try {
        const response = await APIGetFamilies(
          () => {},
          1,
          term,
          institutionId,
          FAMILY_SEARCH_PAGE_SIZE,
        );
        if (cancelled) return;

        if (response?.status === 200) {
          const families = response.data?.data?.families || [];
          setFamilyResults(
            families.map((family) => {
              const students = getAudienceStudentNamesFromFamily(family);
              return {
                id: family.familyId || family._id,
                title: getFamilyShortLabel(family),
                subtitle: getFamilyDisplayLabel(family),
                students,
                studentsModalTitle: getFamilyShortLabel(family),
                studentsModalSubtitle: "Children in this family",
                raw: family,
              };
            }),
          );
        } else {
          setFamilyResults([]);
        }
      } catch {
        if (!cancelled) {
          setFamilyResults([]);
        }
      } finally {
        if (!cancelled) {
          setFamilyLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [familySearch, institutionId, targetAudience]);

  useEffect(() => {
    if (targetAudience !== "family" || !institutionId) return undefined;

    const labelsFromDetails = (input.familyDetails || []).reduce(
      (labels, detail) => {
        const id = detail?.familyId || detail?._id;
        if (id && !familyLabels[id]) {
          labels[id] = getFamilyShortLabel(detail);
        }
        return labels;
      },
      {},
    );

    if (Object.keys(labelsFromDetails).length > 0) {
      setFamilyLabels((prev) => ({ ...prev, ...labelsFromDetails }));
    }

    const selectedIds = input.familyIds || [];
    const missingIds = selectedIds.filter(
      (id) =>
        !familyLabels[id] &&
        !labelsFromDetails[id] &&
        !attemptedFamilyLabelIdsRef.current.has(id),
    );
    if (missingIds.length === 0) return undefined;

    missingIds.forEach((id) => attemptedFamilyLabelIdsRef.current.add(id));

    let cancelled = false;

    (async () => {
      try {
        const response = await APIGetFamilies(
          () => {},
          1,
          "",
          institutionId,
          FAMILY_SEARCH_PAGE_SIZE,
        );
        if (cancelled || response?.status !== 200) return;

        const families = response.data?.data?.families || [];
        const updates = {};

        families.forEach((family) => {
          const id = family.familyId || family._id;
          if (missingIds.includes(id)) {
            updates[id] = getFamilyShortLabel(family);
          }
        });

        if (Object.keys(updates).length > 0) {
          setFamilyLabels((prev) => ({ ...prev, ...updates }));
        }
      } catch {
        // ignore label hydration errors
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    targetAudience,
    institutionId,
    input.familyIds,
    input.familyDetails,
    familyLabels,
    setFamilyLabels,
  ]);

  useEffect(() => {
    if (targetAudience !== "stop" || !institutionId) return undefined;

    const term = stopSearch.trim();
    if (term.length < 2) {
      setStopResults([]);
      return undefined;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      setStopLoading(true);
      try {
        const response = await APIGetStops(
          1,
          ACTIVE_STOP_FILTERS,
          () => {},
          institutionId,
          term,
          STOP_SEARCH_PAGE_SIZE,
        );
        if (cancelled) return;

        if (response?.status === 200) {
          const stops = response.data?.data?.stops || [];
          setStopResults(
            stops.map((stop) => {
              const students = getAudienceStudentNamesFromStop(stop);
              return {
                id: stop._id || stop.id,
                title: getStopShortLabel(stop),
                subtitle: getStopDisplayLabel(stop),
                students,
                studentsModalTitle: getStopShortLabel(stop),
                studentsModalSubtitle: stop?.address?.trim() || null,
                raw: stop,
              };
            }),
          );
        } else {
          setStopResults([]);
        }
      } catch {
        if (!cancelled) {
          setStopResults([]);
        }
      } finally {
        if (!cancelled) {
          setStopLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [stopSearch, institutionId, targetAudience]);

  useEffect(() => {
    if (targetAudience !== "stop" || !institutionId) return undefined;

    const labelsFromDetails = (input.stopDetails || []).reduce(
      (labels, detail) => {
        const id = detail?.stopId || detail?._id || detail?.id;
        if (id && !stopLabels[id]) {
          labels[id] = getStopShortLabel(detail);
        }
        return labels;
      },
      {},
    );

    if (Object.keys(labelsFromDetails).length > 0) {
      setStopLabels((prev) => ({ ...prev, ...labelsFromDetails }));
    }

    const selectedIds = input.stopIds || [];
    const missingIds = selectedIds.filter(
      (id) =>
        !stopLabels[id] &&
        !labelsFromDetails[id] &&
        !attemptedStopLabelIdsRef.current.has(id),
    );
    if (missingIds.length === 0) return undefined;

    missingIds.forEach((id) => attemptedStopLabelIdsRef.current.add(id));

    let cancelled = false;

    (async () => {
      try {
        const response = await APIGetStops(
          1,
          ACTIVE_STOP_FILTERS,
          () => {},
          institutionId,
          "",
          STOP_SEARCH_PAGE_SIZE,
        );
        if (cancelled || response?.status !== 200) return;

        const stops = response.data?.data?.stops || [];
        const updates = {};

        stops.forEach((stop) => {
          const id = stop._id || stop.id;
          if (missingIds.includes(id)) {
            updates[id] = getStopShortLabel(stop);
          }
        });

        if (Object.keys(updates).length > 0) {
          setStopLabels((prev) => ({ ...prev, ...updates }));
        }
      } catch {
        // ignore label hydration errors
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    targetAudience,
    institutionId,
    input.stopIds,
    input.stopDetails,
    stopLabels,
    setStopLabels,
  ]);

  useEffect(() => {
    setScheduleSearch("");
  }, [scheduleLookup?.scheduleLookup, targetAudience]);

  if (targetAudience === "all") {
    return (
      <div className="campaign-audience-panel campaign-audience-panel--info">
        <Text
          text="All active parents in this institution will receive this campaign."
          size="0.8125rem"
          color="#475467"
          weight="400"
        />
      </div>
    );
  }

  if (targetAudience === "schedule") {
    const selectedIds = input.scheduleIds || [];
    const scheduleDate = scheduleLookup?.scheduleLookup;

    return (
      <div className="campaign-audience-panel">
        <div className="campaign-audience-panel__label-row">
          <Text
            text="Scheduled trips"
            size="0.8125rem"
            color="#344054"
            weight="600"
          />
          <Text
            text="Choose one or more scheduled trips."
            size="0.75rem"
            color="#667085"
            weight="400"
          />
        </div>
        {!readOnly ? (
          <CustomDatePicker
            disable={readOnly}
            label="Schedule date"
            placeholder="Select a date"
            name="scheduleLookup"
            error={Boolean(error.scheduleAudience)}
            setError={() => {}}
            input={scheduleLookup}
            setInput={setScheduleLookup}
            errorMessage=""
            width={datetimeWidth}
          />
        ) : null}
        {!readOnly ? (
          <SearchInput
            containerWidth="100%"
            text="Search by trip name…"
            value={scheduleSearch}
            onChangeText={(e) => setScheduleSearch(e.target.value)}
          />
        ) : null}
        {!readOnly ? (
          <SearchResultList
            items={scheduleResultItems}
            selectedIds={selectedIds}
            disabled={readOnly || !scheduleDate}
            loading={scheduleLoading}
            emptyMessage={
              !scheduleDate
                ? "Select a date to browse trips."
                : scheduleSearch.trim()
                  ? "No trips match your search."
                  : "No trips scheduled on this date."
            }
            onToggle={(item) => toggleSchedule(item.raw)}
          />
        ) : null}
        {selectedIds.length > 0 ? (
          <SelectedChipsGroup count={selectedIds.length}>
            {selectedIds.map((id) => (
              <SelectedChip
                key={id}
                label={getScheduleChipLabel(id)}
                disabled={readOnly}
                onRemove={() =>
                  setInput((prev) => {
                    const nextIds = (prev.scheduleIds || []).filter(
                      (item) => item !== id,
                    );
                    return {
                      ...prev,
                      scheduleIds: nextIds,
                      scheduleId: nextIds[0] || "",
                    };
                  })
                }
              />
            ))}
          </SelectedChipsGroup>
        ) : null}
        {error.scheduleAudience ? (
          <p className="campaign-form-error visible">{error.scheduleAudience}</p>
        ) : null}
      </div>
    );
  }

  if (targetAudience === "tripType") {
    return (
      <div className="campaign-audience-panel">
        <div className="campaign-audience-panel__label-row">
          <Text
            text="Trip types"
            size="0.8125rem"
            color="#344054"
            weight="600"
          />
          <Text
            text="Select one or more trip types. Parents of students on matching trips will receive this campaign."
            size="0.75rem"
            color="#667085"
            weight="400"
          />
        </div>
        <div className="campaign-trip-type-chips">
          {tripTypeOptions.length === 0 ? (
            <p className="campaign-audience-panel__hint">
              No trip types configured for this institution.
            </p>
          ) : (
            tripTypeOptions.map((type) => {
              const selected = (input.tripTypeIds || []).includes(type.id);
              return (
                <button
                  key={type.id}
                  type="button"
                  disabled={readOnly}
                  className={`campaign-trip-type-chip${selected ? " campaign-trip-type-chip--selected" : ""}`}
                  onClick={() => toggleTripType(type.id)}
                >
                  {type.label}
                </button>
              );
            })
          )}
        </div>
        <div className="campaign-audience-panel__scope">
          <Text
            text="Applies to"
            size="0.8125rem"
            color="#344054"
            weight="600"
          />
          <div className="campaign-audience-panel__scope-radios">
            <RadioButton
              name="tripTypeScope"
              value="all"
              text="Students on any upcoming trip of the selected types"
              checked={input.tripTypeScope !== "date"}
              disabled={readOnly}
              onChange={(e) =>
                setInput((prev) => ({
                  ...prev,
                  tripTypeScope: e.target.value,
                  tripTypeDate: "",
                }))
              }
            />
            <RadioButton
              name="tripTypeScope"
              value="date"
              text="Students on scheduled trips of the selected types on a specific date"
              checked={input.tripTypeScope === "date"}
              disabled={readOnly}
              onChange={(e) =>
                setInput((prev) => ({
                  ...prev,
                  tripTypeScope: e.target.value,
                }))
              }
            />
          </div>
          {input.tripTypeScope === "date" ? (
            <CustomDatePicker
              disable={readOnly}
              label="Date"
              placeholder="Select a date"
              name="tripTypeDate"
              error={Boolean(error.scheduleAudience)}
              setError={() => {}}
              input={input}
              setInput={setInput}
              errorMessage=""
              width={datetimeWidth}
            />
          ) : null}
        </div>
        {error.scheduleAudience ? (
          <p className="campaign-form-error visible">{error.scheduleAudience}</p>
        ) : null}
      </div>
    );
  }

  if (targetAudience === "family") {
    const selectedIds = input.familyIds || [];
    return (
      <div className="campaign-audience-panel">
        <div className="campaign-audience-panel__label-row">
          <Text
            text="Families"
            size="0.8125rem"
            color="#344054"
            weight="600"
          />
          <Text
            text="Choose one or more families."
            size="0.75rem"
            color="#667085"
            weight="400"
          />
        </div>
        {!readOnly ? (
          <SearchInput
            containerWidth="100%"
            text="Search by parent or child name…"
            value={familySearch}
            onChangeText={(e) => setFamilySearch(e.target.value)}
          />
        ) : null}
        {!readOnly ? (
          <SearchResultList
            items={familyResults}
            selectedIds={selectedIds}
            disabled={readOnly}
            loading={familyLoading}
            onViewStudents={openStudentsModal}
            emptyMessage={
              familySearch.trim().length < 2
                ? "Type at least 2 characters to search families."
                : "No families match your search."
            }
            onToggle={(item) => toggleFamily(item.raw)}
          />
        ) : null}
        {selectedIds.length > 0 ? (
          <SelectedChipsGroup count={selectedIds.length}>
            {selectedIds.map((id) => (
              <SelectedChip
                key={id}
                label={resolveFamilyLabel(id, {
                  familyLabels,
                  familyDetails: input.familyDetails,
                })}
                disabled={readOnly}
                onRemove={() =>
                  setInput((prev) => ({
                    ...prev,
                    familyIds: (prev.familyIds || []).filter(
                      (item) => item !== id,
                    ),
                  }))
                }
              />
            ))}
          </SelectedChipsGroup>
        ) : null}
        {error.scheduleAudience ? (
          <p className="campaign-form-error visible">{error.scheduleAudience}</p>
        ) : null}
        <CampaignAudienceStudentsModal
          isOpen={Boolean(studentsModal)}
          onClose={closeStudentsModal}
          title={studentsModal?.title}
          subtitle={studentsModal?.subtitle}
          students={studentsModal?.students || []}
        />
      </div>
    );
  }

  if (targetAudience === "stop") {
    const selectedIds = input.stopIds || [];
    return (
      <div className="campaign-audience-panel">
        <div className="campaign-audience-panel__label-row">
          <Text
            text="Stops"
            size="0.8125rem"
            color="#344054"
            weight="600"
          />
          <Text
            text="Parents of students at the stops you choose."
            size="0.75rem"
            color="#667085"
            weight="400"
          />
        </div>
        {!readOnly ? (
          <SearchInput
            containerWidth="100%"
            text="Search by stop name…"
            value={stopSearch}
            onChangeText={(e) => setStopSearch(e.target.value)}
          />
        ) : null}
        {!readOnly ? (
          <SearchResultList
            items={stopResults}
            selectedIds={selectedIds}
            disabled={readOnly}
            loading={stopLoading}
            onViewStudents={openStudentsModal}
            emptyMessage={
              stopSearch.trim().length < 2
                ? "Type at least 2 characters to search stops."
                : "No stops match your search."
            }
            onToggle={(item) => toggleStop(item.raw)}
          />
        ) : null}
        {selectedIds.length > 0 ? (
          <SelectedChipsGroup count={selectedIds.length}>
            {selectedIds.map((id) => (
              <SelectedChip
                key={id}
                label={resolveStopLabel(id, {
                  stopLabels,
                  stopDetails: input.stopDetails,
                })}
                disabled={readOnly}
                onRemove={() =>
                  setInput((prev) => ({
                    ...prev,
                    stopIds: (prev.stopIds || []).filter(
                      (item) => item !== id,
                    ),
                  }))
                }
              />
            ))}
          </SelectedChipsGroup>
        ) : null}
        {error.scheduleAudience ? (
          <p className="campaign-form-error visible">{error.scheduleAudience}</p>
        ) : null}
        <CampaignAudienceStudentsModal
          isOpen={Boolean(studentsModal)}
          onClose={closeStudentsModal}
          title={studentsModal?.title}
          subtitle={studentsModal?.subtitle}
          students={studentsModal?.students || []}
        />
      </div>
    );
  }

  if (targetAudience === "campus") {
    const selectedCampus = resolveCampusValue(input);

    return (
      <div className="campaign-audience-panel">
        <div className="campaign-audience-panel__label-row">
          <Text
            text="Campus"
            size="0.8125rem"
            color="#344054"
            weight="600"
          />
          <Text
            text="Select primary or secondary. Parents of students in the chosen campus will receive this campaign."
            size="0.75rem"
            color="#667085"
            weight="400"
          />
        </div>
        <div className="campaign-trip-type-chips">
          {CAMPAIGN_CAMPUS_GRADE_OPTIONS.map((option) => {
            const selected = selectedCampus === option.value;
            return (
              <button
                key={option.value}
                type="button"
                disabled={readOnly}
                className={`campaign-trip-type-chip${selected ? " campaign-trip-type-chip--selected" : ""}`}
                onClick={() => selectCampus(option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {error.scheduleAudience ? (
          <p className="campaign-form-error visible">{error.scheduleAudience}</p>
        ) : null}
      </div>
    );
  }

  return null;
}

CampaignAudiencePanel.propTypes = {
  targetAudience: PropTypes.string,
  input: PropTypes.object.isRequired,
  setInput: PropTypes.func.isRequired,
  mode: PropTypes.string,
  error: PropTypes.object,
  scheduleLookup: PropTypes.object,
  setScheduleLookup: PropTypes.func,
  scheduleData: PropTypes.array,
  scheduleLoading: PropTypes.bool,
  tripTypes: PropTypes.array,
  institutionId: PropTypes.string,
  datetimeWidth: PropTypes.string,
  scheduleLabels: PropTypes.object,
  setScheduleLabels: PropTypes.func,
  familyLabels: PropTypes.object,
  setFamilyLabels: PropTypes.func,
  stopLabels: PropTypes.object,
  setStopLabels: PropTypes.func,
};
